var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import './PaginatedTable.scss';
import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { SonarTable } from '@ui/SonarTable';
import { When } from 'react-if';
import { Button, Space } from 'antd';
import { ConfirmDeleteButton } from '@ui/modals';
import { KeyPagination, keyPaginationDefaultQueryParams } from '@ui/KeyPagination';
import { SonarV3SortDirection } from '@sonar-software/sonar-ts-types';
import React, { useMemo, useRef } from 'react';
import { useCurrentUser } from '@hooks';
const sortDirectionMap = {
    ascend: SonarV3SortDirection.Ascending,
    descend: SonarV3SortDirection.Descending
};
export const PaginatedTable = (_a) => {
    var { autohidePagination, canDeleteRow = () => true, canEditRow = () => true, className, columns, defaultPageSize = keyPaginationDefaultQueryParams.count, deletePrompt, expandable, getPopupContainer, headerSize = 'normal', id, loading, onChange, onDelete, onEdit, onRow, resource, rowClassName, rowKey, scroll, showSizeChanger, sortBy, sortDirection, sticky } = _a, rest = __rest(_a, ["autohidePagination", "canDeleteRow", "canEditRow", "className", "columns", "defaultPageSize", "deletePrompt", "expandable", "getPopupContainer", "headerSize", "id", "loading", "onChange", "onDelete", "onEdit", "onRow", "resource", "rowClassName", "rowKey", "scroll", "showSizeChanger", "sortBy", "sortDirection", "sticky"]);
    const { isReadOnly } = useCurrentUser();
    const scrollableListRef = useRef(null);
    const managedColumns = useMemo(() => {
        let updatedColumns = columns;
        if ((onEdit || onDelete) && !isReadOnly) {
            updatedColumns = [
                ...updatedColumns,
                {
                    className: 'table-actions',
                    key: 'table-actions',
                    render: (_, record) => React.createElement(Space, null,
                        React.createElement(When, { condition: canEditRow === null || canEditRow === void 0 ? void 0 : canEditRow(record) }, onEdit &&
                            React.createElement(Button, { id: `edit-record-button-${rowKey(record)}`, onClick: () => onEdit(record), type: 'link' }, COPY_SPECIFIC.EDIT)),
                        React.createElement(When, { condition: canDeleteRow === null || canDeleteRow === void 0 ? void 0 : canDeleteRow(record) }, onDelete && React.createElement(ConfirmDeleteButton, Object.assign({ buttonId: `delete-record-button-${rowKey(record)}`, getPopupContainer: getPopupContainer, onConfirm: () => onDelete(record), placement: 'bottomRight', title: typeof deletePrompt === 'function' ? deletePrompt(record) : deletePrompt, type: 'link' }, rest)))),
                    width: 100
                }
            ];
        }
        if (sortBy && Number.isInteger(sortDirection)) {
            updatedColumns = updatedColumns.map((column) => {
                if (column.dataIndex === sortBy) {
                    return Object.assign(Object.assign({}, column), { sortOrder: sortDirection ? 'descend' : 'ascend' });
                }
                return column;
            });
        }
        return updatedColumns;
    }, [
        canEditRow,
        columns,
        onDelete,
        onEdit,
        isReadOnly,
        sortBy,
        sortDirection,
        canDeleteRow,
        rowKey,
        getPopupContainer,
        deletePrompt,
        rest
    ]);
    return (React.createElement("div", { className: classNames('paginated-table', {
            'small-header': headerSize === 'small'
        }, className), id: id },
        React.createElement(SonarTable, { bordered: false, columns: managedColumns, dataSource: resource === null || resource === void 0 ? void 0 : resource.results, expandable: expandable, loading: loading, onChange: (_config, _filters, sorters) => {
                onChange({
                    sortBy: sorters.columnKey,
                    sortDirection: sorters.order ? sortDirectionMap[sorters.order] : undefined
                });
            }, onRow: onRow, pagination: false, ref: scrollableListRef, rowClassName: rowClassName, rowKey: rowKey, scroll: Object.assign({ y: 'content' }, scroll), sticky: sticky }),
        React.createElement(KeyPagination, { autoHide: autohidePagination, defaultPageSize: defaultPageSize, listRef: scrollableListRef, onChange: onChange, resource: resource, showSizeChanger: showSizeChanger })));
};
